// klaes-app/src/components/UiPropsModal/UiPropsModal.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import styles from './UiPropsModal.module.css';

const AVAILABLE_WIDGETS = {
  'film-favs': { label: 'Film-Favoriten', description: 'Lieblingsfilme verwalten' },
  'music-favs': { label: 'Musik-Favoriten', description: 'Musik teilen' },
  'family-calendar': { label: 'Familienkalender', description: 'Gemeinsame Termine' },
  'family-chat': { label: 'Familien-Chat', description: 'Chat mit der Familie' },
  'schwurbler-newsfeed': { label: 'Newsfeed', description: 'Aktuelle Nachrichten' }
};

const UiPropsModal = ({ onClose }) => {
  const { token } = useAuth();
  const { preferences, savePreferences } = usePreferences();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Lokale State für die Formulardaten
  const [accentColor, setAccentColor] = useState(preferences.accentColor);
  const [fontSize, setFontSize] = useState(preferences.fontSize);
  const [theme, setTheme] = useState(preferences.theme || {});
  const [widgetOrder, setWidgetOrder] = useState(preferences.widgets?.order || []);
  const [widgetVisibility, setWidgetVisibility] = useState(preferences.widgets?.visibility || {});

  // Laden der Daten aus dem Context
  useEffect(() => {
    if (preferences) {
      setAccentColor(preferences.accentColor || '#6366f1');
      setFontSize(preferences.fontSize || 100);
      setTheme(preferences.theme || {});
      setWidgetOrder(preferences.widgets?.order || [
        'film-favs', 'music-favs', 'family-calendar', 'family-chat', 'schwurbler-newsfeed'
      ]);
      setWidgetVisibility(preferences.widgets?.visibility || {
        'film-favs': true,
        'music-favs': true,
        'family-calendar': true,
        'family-chat': true,
        'schwurbler-newsfeed': true
      });
      setLoading(false);
    }
  }, [preferences]);

  const handleThemeChange = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
  };

  const toggleVisibility = (widgetId) => {
    setWidgetVisibility(prev => ({
      ...prev,
      [widgetId]: !prev[widgetId]
    }));
  };

  const moveWidget = (index, direction) => {
    const newOrder = [...widgetOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setWidgetOrder(newOrder);
  };

  const handleReset = () => {
    setAccentColor('#6366f1');
    setFontSize(100);
    setTheme({});
    setWidgetOrder([
      'film-favs', 'music-favs', 'family-calendar', 'family-chat', 'schwurbler-newsfeed'
    ]);
    setWidgetVisibility({
      'film-favs': true,
      'music-favs': true,
      'family-calendar': true,
      'family-chat': true,
      'schwurbler-newsfeed': true
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const newProps = {
        accentColor,
        fontSize,
        theme: Object.keys(theme).length > 0 ? theme : null,
        widgets: {
          order: widgetOrder,
          visibility: widgetVisibility
        }
      };
      
      await savePreferences(newProps);
      setSuccess('Einstellungen gespeichert!');
      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 100);
    } catch (err) {
      setError(err.error || 'Fehler beim Speichern');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal}>Lade Einstellungen...</div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>UI Einstellungen</h3>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        {/* Allgemeine UI (Akzent, Schrift) */}
        <div className={styles.section}>
          <h4>Allgemeine UI</h4>
          <div className={styles.settingGroup}>
            <label>Akzentfarbe</label>
            <input
              type="color"
              value={accentColor}
              onChange={(e) => setAccentColor(e.target.value)}
              className={styles.colorPicker}
            />
          </div>
          <div className={styles.settingGroup}>
            <label>Schriftgröße: {fontSize}%</label>
            <input
              type="range"
              min="80"
              max="140"
              step="5"
              value={fontSize}
              onChange={(e) => setFontSize(parseInt(e.target.value, 10))}
              className={styles.slider}
            />
          </div>
        </div>

        {/* Farbthema */}
        <div className={styles.section}>
          <h4>Farbthema (optional)</h4>
          <div className={styles.colorGrid}>
            <label>
              Hintergrund (primär)
              <input 
                type="color" 
                value={theme.bgPrimary || ''} 
                onChange={e => handleThemeChange('bgPrimary', e.target.value)} 
              />
            </label>
            <label>
              Hintergrund (sekundär)
              <input 
                type="color" 
                value={theme.bgSecondary || ''} 
                onChange={e => handleThemeChange('bgSecondary', e.target.value)} 
              />
            </label>
            <label>
              Text (primär)
              <input 
                type="color" 
                value={theme.textPrimary || ''} 
                onChange={e => handleThemeChange('textPrimary', e.target.value)} 
              />
            </label>
            <label>
              Button‑Hintergrund
              <input 
                type="color" 
                value={theme.buttonBg || ''} 
                onChange={e => handleThemeChange('buttonBg', e.target.value)} 
              />
            </label>
            <label>
              Button‑Text
              <input 
                type="color" 
                value={theme.buttonText || ''} 
                onChange={e => handleThemeChange('buttonText', e.target.value)} 
              />
            </label>
          </div>
          <p className={styles.hint}>Leer lassen = Standardwerte des Themes verwenden</p>
        </div>

        {/* Widgets */}
        <div className={styles.section}>
          <h4>Widgets anzeigen / sortieren</h4>
          <div className={styles.widgetList}>
            {widgetOrder.map((widgetId, idx) => {
              const widget = AVAILABLE_WIDGETS[widgetId];
              if (!widget) return null;
              return (
                <div key={widgetId} className={styles.widgetItem}>
                  <label className={styles.widgetCheckbox}>
                    <input 
                      type="checkbox" 
                      checked={widgetVisibility[widgetId] !== false} 
                      onChange={() => toggleVisibility(widgetId)} 
                    />
                    <strong>{widget.label}</strong>
                    <span className={styles.widgetDesc}>{widget.description}</span>
                  </label>
                  <div className={styles.sortButtons}>
                    <button onClick={() => moveWidget(idx, 'up')} disabled={idx === 0}>▲</button>
                    <button onClick={() => moveWidget(idx, 'down')} disabled={idx === widgetOrder.length - 1}>▼</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <button className={styles.resetBtn} onClick={handleReset}>
            Auf Standard zurücksetzen
          </button>
        </div>

        <div className={styles.buttonGroup}>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
          <button className={styles.closeBtn} onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

export default UiPropsModal;